import type { MediaSelectorImageType } from '../components/content/mediaImage/PuxPicture'
import type { localizedPathType } from '../components/PuxLanguageSelector/PuxLanguageSelector'
import type { PuxWysiwygHtmlBodyType } from '../components/PuxWysiwyg/PuxWysiwyg'
import type { PuxMetaTagsType } from './PuxMetaTags'

export interface ErrorPageLinkType {
  internal?: boolean[] | null
  text?: string[] | null
  url?: string[] | null
}

export interface ErrorPageDataType {
  contentItemId?: string
  errorPageButton?: ErrorPageLinkType | null
  errorPageHeading?: PuxWysiwygHtmlBodyType | string | null
  errorPageImage?: MediaSelectorImageType | null
  localization?: {
    localizations?: localizedPathType[]
    culture?: string
  } | null
  puxMetaTags?: Partial<PuxMetaTagsType> | null
}

export interface ResolvedErrorPageData extends ErrorPageDataType {
  errorPageHeading: PuxWysiwygHtmlBodyType
  errorPageButton: {
    internal: [boolean]
    text: [string]
    url: [string]
  }
  puxMetaTags: Partial<PuxMetaTagsType> & {
    metaTagsTitle: PuxWysiwygHtmlBodyType
  }
}

const nonEmpty = (value?: string | null): string | undefined =>
  value?.trim() ? value : undefined

const getHeading = (page?: ErrorPageDataType): string | undefined =>
  typeof page?.errorPageHeading === `string`
    ? nonEmpty(page.errorPageHeading)?.replace(/\r?\n/g, `<br>`)
    : nonEmpty(page?.errorPageHeading?.html)

export function resolveErrorPage(
  pages: readonly ErrorPageDataType[] | null | undefined,
  culture: string,
  homepageUrl: string
): { pageData: ResolvedErrorPageData; fallbackFields: string[] } {
  const localized = pages?.find(
    (page) =>
      page.localization?.culture?.toLowerCase() === culture.toLowerCase()
  )
  const english = pages?.find(
    (page) => page.localization?.culture?.toLowerCase() === `en-gb`
  )
  const fallbackFields: string[] = []
  const resolveText = (
    field: string,
    localValue: string | undefined,
    englishValue: string | undefined,
    defaultValue: string
  ): string => {
    if (localValue) return localValue
    fallbackFields.push(field)
    return englishValue ?? defaultValue
  }
  const heading = resolveText(
    `errorPageHeading`,
    getHeading(localized),
    getHeading(english),
    `We can't find what you're looking for.`
  )
  const buttonText = resolveText(
    `errorPageButton.text`,
    nonEmpty(localized?.errorPageButton?.text?.[0]),
    nonEmpty(english?.errorPageButton?.text?.[0]),
    `Back to the homepage`
  )
  const title = resolveText(
    `puxMetaTags.metaTagsTitle`,
    nonEmpty(localized?.puxMetaTags?.metaTagsTitle?.html),
    nonEmpty(english?.puxMetaTags?.metaTagsTitle?.html),
    `Page not found - `
  )
  const buttonUrl = nonEmpty(localized?.errorPageButton?.url?.[0])

  return {
    fallbackFields,
    pageData: {
      ...(localized ?? english),
      errorPageHeading: { html: heading },
      errorPageButton: {
        text: [buttonText],
        url: [buttonUrl ?? homepageUrl],
        internal: [
          buttonUrl ? localized?.errorPageButton?.internal?.[0] ?? true : true,
        ],
      },
      // Keep locale-specific metadata (especially canonical URLs) in its locale.
      puxMetaTags: {
        ...localized?.puxMetaTags,
        metaTagsTitle: { html: title },
      },
    },
  }
}
